// @ts-ignore
import { hasProp, assert } from "@utils/assertion";
// @ts-ignore
import { isArray } from "@utils/arrayUtils";
// @ts-ignore
import { parseJsonWithNumbersAsBigint, renderJsonWithNumbersAsBigint, type ParserResult } from "@utils/json";
// @ts-ignore
import { toBase64 } from "@utils/blobUtils";
// @ts-ignore
import { DateTime } from "luxon";
// @ts-ignore
import { type ReadonlyUint8Array } from "@utils/arrayUtils";
import type { Language, Tag, ContentMetadataCommon } from "../schema";
import { serialize_Language_internal, deserialize_Language_internal } from "./Language";
import { serialize_Tag_internal, deserialize_Tag_internal } from "./Tag";

export function serialize_ContentMetadataCommon_internal(value: ContentMetadataCommon): unknown {
  return ((value: {
    activatedAt: DateTime<true> | null;
    audioActive: boolean;
    coverUri: string;
    description: string | null;
    inactive: boolean;
    language: Language;
    leadIn: string | null;
    link: string | null;
    tags: Tag[];
    title: string;
  }): unknown => {
    return {
      activatedAt: ((value: DateTime<true> | null): unknown => {
        if (value === null) {
          return null;
        }
        return value.toMillis();
      })(value["activatedAt"]),
      audioActive: ((value: boolean): unknown => {
        return value;
      })(value["audioActive"]),
      coverUri: ((value: string): unknown => {
        return value;
      })(value["coverUri"]),
      description: ((value: string | null): unknown => {
        if (value === null) {
          return null;
        }
        return value;
      })(value["description"]),
      inactive: ((value: boolean): unknown => {
        return value;
      })(value["inactive"]),
      language: ((value: Language): unknown => {
        return serialize_Language_internal(value);
      })(value["language"]),
      leadIn: ((value: string | null): unknown => {
        if (value === null) {
          return null;
        }
        return value;
      })(value["leadIn"]),
      link: ((value: string | null): unknown => {
        if (value === null) {
          return null;
        }
        return value;
      })(value["link"]),
      tags: ((value: Tag[]): unknown => {
        return value.map((value: Tag): unknown => {
          return serialize_Tag_internal(value);
        });
      })(value["tags"]),
      title: ((value: string): unknown => {
        return value;
      })(value["title"]),
    };
  })(value);
}
export function serialize_ContentMetadataCommon(obj: ContentMetadataCommon): string {
  return renderJsonWithNumbersAsBigint(serialize_ContentMetadataCommon_internal(obj));
}
export function deserialize_ContentMetadataCommon_internal(value: unknown): ParserResult<ContentMetadataCommon> {
  return ((
    value: unknown,
  ): ParserResult<{
    activatedAt: DateTime<true> | null;
    audioActive: boolean;
    coverUri: string;
    description: string | null;
    inactive: boolean;
    language: Language;
    leadIn: string | null;
    link: string | null;
    tags: Tag[];
    title: string;
  }> => {
    if (typeof value !== "object") {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon]: '${value}' expected to be of type 'object'.` };
    }
    if (value === null) {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon]: '${value}' expected to not be null.` };
    }
    if (!hasProp(value, "activatedAt")) {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/activatedAt]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'activatedAt'.` };
    }
    const parsed_activatedAt = ((): ParserResult<DateTime<true> | null> => {
      return ((value: unknown): ParserResult<DateTime<true> | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "number") {
          return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/activatedAt]: '${value}' expected to be of type number.` };
        }
        const parsed = DateTime.fromMillis(value);
        if (!parsed.isValid) {
          return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/activatedAt]: '${value}' expected to be valid DateTime.` };
        }
        return { success: true, value: parsed };
      })(value["activatedAt"]);
    })();
    if (!parsed_activatedAt.success) {
      return { success: false, reason: parsed_activatedAt.reason };
    }
    if (!hasProp(value, "audioActive")) {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/audioActive]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'audioActive'.` };
    }
    const parsed_audioActive = ((): ParserResult<boolean> => {
      return ((value: unknown): ParserResult<boolean> => {
        if (typeof value !== "boolean") {
          return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/audioActive]: '${value}' expected to be of type boolean.` };
        }
        return { success: true, value: value };
      })(value["audioActive"]);
    })();
    if (!parsed_audioActive.success) {
      return { success: false, reason: parsed_audioActive.reason };
    }
    if (!hasProp(value, "coverUri")) {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/coverUri]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'coverUri'.` };
    }
    const parsed_coverUri = ((): ParserResult<string> => {
      return ((value: unknown): ParserResult<string> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/coverUri]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["coverUri"]);
    })();
    if (!parsed_coverUri.success) {
      return { success: false, reason: parsed_coverUri.reason };
    }
    if (!hasProp(value, "description")) {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/description]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'description'.` };
    }
    const parsed_description = ((): ParserResult<string | null> => {
      return ((value: unknown): ParserResult<string | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/description]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["description"]);
    })();
    if (!parsed_description.success) {
      return { success: false, reason: parsed_description.reason };
    }
    if (!hasProp(value, "inactive")) {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/inactive]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'inactive'.` };
    }
    const parsed_inactive = ((): ParserResult<boolean> => {
      return ((value: unknown): ParserResult<boolean> => {
        if (typeof value !== "boolean") {
          return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/inactive]: '${value}' expected to be of type boolean.` };
        }
        return { success: true, value: value };
      })(value["inactive"]);
    })();
    if (!parsed_inactive.success) {
      return { success: false, reason: parsed_inactive.reason };
    }
    if (!hasProp(value, "language")) {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/language]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'language'.` };
    }
    const parsed_language = ((): ParserResult<Language> => {
      return ((value: unknown): ParserResult<Language> => {
        return deserialize_Language_internal(value);
      })(value["language"]);
    })();
    if (!parsed_language.success) {
      return { success: false, reason: parsed_language.reason };
    }
    if (!hasProp(value, "leadIn")) {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/leadIn]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'leadIn'.` };
    }
    const parsed_leadIn = ((): ParserResult<string | null> => {
      return ((value: unknown): ParserResult<string | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/leadIn]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["leadIn"]);
    })();
    if (!parsed_leadIn.success) {
      return { success: false, reason: parsed_leadIn.reason };
    }
    if (!hasProp(value, "link")) {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/link]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'link'.` };
    }
    const parsed_link = ((): ParserResult<string | null> => {
      return ((value: unknown): ParserResult<string | null> => {
        if (value === null) {
          return { success: true, value: null };
        }
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/link]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["link"]);
    })();
    if (!parsed_link.success) {
      return { success: false, reason: parsed_link.reason };
    }
    if (!hasProp(value, "tags")) {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/tags]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'tags'.` };
    }
    const parsed_tags = ((): ParserResult<Tag[]> => {
      return ((value: unknown): ParserResult<Tag[]> => {
        if (!isArray(value)) {
          return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/tags]: '${value}' expected to be an array.` };
        }
        const parsed = value.map((value: unknown): ParserResult<Tag> => {
          return deserialize_Tag_internal(value);
        });
        if (parsed.find((value) => !value.success) !== undefined) {
          return {
            success: false,
            reason: `[#/components/schemas/ContentMetadataCommon/tags]: Reason(s): '${parsed
              .filter((p) => !p.success)
              .map((p) => p.reason)
              .join(", ")}'.`,
          };
        }
        return {
          success: true,
          value: parsed.map((entry) => {
            assert(entry.success == true);
            return entry.value;
          }),
        };
      })(value["tags"]);
    })();
    if (!parsed_tags.success) {
      return { success: false, reason: parsed_tags.reason };
    }
    if (!hasProp(value, "title")) {
      return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/title]: '${renderJsonWithNumbersAsBigint(value)}' expected to have property 'title'.` };
    }
    const parsed_title = ((): ParserResult<string> => {
      return ((value: unknown): ParserResult<string> => {
        if (typeof value !== "string") {
          return { success: false, reason: `[#/components/schemas/ContentMetadataCommon/title]: '${value}' expected to be of type string.` };
        }
        return { success: true, value: value };
      })(value["title"]);
    })();
    if (!parsed_title.success) {
      return { success: false, reason: parsed_title.reason };
    }
    return {
      success: true,
      value: {
        activatedAt: parsed_activatedAt.value,
        audioActive: parsed_audioActive.value,
        coverUri: parsed_coverUri.value,
        description: parsed_description.value,
        inactive: parsed_inactive.value,
        language: parsed_language.value,
        leadIn: parsed_leadIn.value,
        link: parsed_link.value,
        tags: parsed_tags.value,
        title: parsed_title.value,
      },
    };
  })(value);
}
export function deserialize_ContentMetadataCommon(str: string): ParserResult<ContentMetadataCommon> {
  return deserialize_ContentMetadataCommon_internal(parseJsonWithNumbersAsBigint(str));
}
